import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search, Sparkles, Video } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { logDev } from "@/lib/ux";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DateTimePicker from "@/components/DateTimePicker";
import MeetingTeamMembers from "@/components/MeetingTeamMembers";

const blank = {
  activity_type: "Follow-up", activity_title: "", activity_note: "",
  due_at: "", assigned_to_user_id: "", priority: "Medium",
};

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
// Today => now + 30 min; future days => that day at 10:00 AM.
const quickDue = (days) => {
  const d = new Date();
  if (days === 0) d.setMinutes(d.getMinutes() + 30);
  else { d.setDate(d.getDate() + days); d.setHours(10, 0, 0, 0); }
  return toLocalInput(d);
};

export default function ActivityModal({ open, onOpenChange, leadId, leadName, activity, repeat, presetType, mode, users = [], onSaved }) {
  const { options, user, can } = useAuth();
  const isEdit = !!activity;
  const isMeeting = mode === "meeting";
  const [form, setForm] = useState(blank);
  const [placeholder, setPlaceholder] = useState("Describe the activity.");
  const [saving, setSaving] = useState(false);
  // lead picker (when no leadId passed)
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const debounce = useRef();
  // meeting-only: internal team members
  const [team, setTeam] = useState([]);
  const titleEdited = useRef(false);

  // Meeting-specific fields appear whenever the activity type is Meeting — in the
  // dedicated Meetings-tab flow (mode) OR the generic popup when type is switched to Meeting.
  const meetingType = form.activity_type === "Meeting";
  const showMeetingFields = isMeeting || meetingType;

  useEffect(() => {
    if (!open) return;
    titleEdited.current = false;
    setTeam([]);
    if (activity) {
      setForm({
        activity_type: activity.activity_type, activity_title: activity.activity_title || "",
        activity_note: activity.activity_note || "",
        due_at: activity.due_at ? new Date(activity.due_at).toISOString().slice(0, 16) : "",
        assigned_to_user_id: activity.assigned_to_user_id || "", priority: activity.priority || "Medium",
      });
      // Prefill saved participants when editing a Meeting activity
      if (activity.activity_type === "Meeting") setTeam(activity.internal_team_member_ids || []);
      titleEdited.current = true; // don't auto-rewrite an existing title
      setPicked(null);
    } else if (repeat) {
      setForm({
        activity_type: repeat.activity_type, activity_title: repeat.activity_title || "",
        activity_note: repeat.activity_note || "", due_at: "",
        assigned_to_user_id: repeat.assigned_to_user_id || "", priority: repeat.priority || "Medium",
      });
      setPicked(repeat.lead_id ? { id: repeat.lead_id, name: repeat.lead_name || "lead" } : null);
    } else {
      setForm({
        ...blank,
        activity_type: isMeeting ? "Meeting" : (presetType || blank.activity_type),
        activity_title: isMeeting || presetType === "Meeting" ? "Meeting with lead" : "",
        due_at: isMeeting ? quickDue(0) : "",
        assigned_to_user_id: "",
      });
      setPicked(null);
    }
    setLeadSearch(""); setLeadResults([]);
  }, [open, activity, repeat, presetType, isMeeting]);

  // Clear participants if the type is switched away from Meeting (generic popup).
  useEffect(() => {
    if (!meetingType) setTeam([]);
  }, [meetingType]);

  useEffect(() => {
    if (!leadId && !isEdit && leadSearch.trim().length > 1) {
      clearTimeout(debounce.current);
      debounce.current = setTimeout(async () => {
        try {
          const { data } = await api.get("/leads", { params: { search: leadSearch, page_size: 8 } });
          setLeadResults(data.data.items);
        } catch (e) { logDev("Lead search failed", e); }
      }, 300);
    } else {
      setLeadResults([]);
    }
    return () => clearTimeout(debounce.current);
  }, [leadSearch, leadId, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickLead = (l) => {
    setPicked(l);
    setLeadResults([]);
    if (showMeetingFields && !titleEdited.current && l?.name) set("activity_title", `Meeting with ${l.name}`);
  };

  const applyTemplate = (tpl) => {
    setForm((f) => ({ ...f, activity_type: tpl.activity_type, activity_title: tpl.activity_title }));
    setPlaceholder(tpl.note_placeholder);
  };

  const save = async () => {
    const targetLead = leadId || picked?.id;
    if (!targetLead && !isEdit) return toast.error(isMeeting ? "Select a lead for this meeting" : "Select a lead for this activity");
    if (!form.activity_title.trim()) return toast.error(isMeeting ? "Meeting title is required" : "Activity title is required");
    setSaving(true);
    try {
      const payload = {
        activity_type: isMeeting ? "Meeting" : form.activity_type, activity_title: form.activity_title,
        activity_note: form.activity_note || null,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        priority: form.priority,
      };
      if (can("reassign") && form.assigned_to_user_id) payload.assigned_to_user_id = form.assigned_to_user_id;
      payload.internal_team_member_ids = meetingType ? team : [];
      if (isEdit) await api.put(`/activities/${activity.id}`, payload);
      else await api.post("/activities", { ...payload, lead_id: targetLead });
      toast.success(isEdit ? "Activity updated" : isMeeting ? "Meeting scheduled successfully" : "Activity created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const title = isEdit ? "Edit Activity" : repeat ? "Repeat Activity" : isMeeting ? "Schedule Meeting" : "New Activity";
  const submitLabel = isEdit ? "Save changes" : isMeeting ? "Schedule Meeting" : "Create activity";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-sky bg-white sm:max-w-lg" data-testid="activity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy">
            {isMeeting && <Video className="h-5 w-5 text-loop" />}{title}
          </DialogTitle>
          <DialogDescription className="sr-only">{isMeeting ? "Schedule a meeting with a lead." : "Schedule or edit a call, follow-up, meeting or task for a lead."}</DialogDescription>
        </DialogHeader>

        {/* Lead context / picker */}
        {leadId ? (
          <p className="rounded-lg bg-mist px-3 py-2 text-sm text-ink/70">For lead: <span className="font-semibold text-navy">{leadName || "this lead"}</span></p>
        ) : !isEdit ? (
          <div>
            <label className="mb-1 block text-sm font-medium text-ink/80">Lead</label>
            {picked ? (
              <div className="flex items-center justify-between rounded-lg border border-loop bg-sky/20 px-3 py-2">
                <span className="text-sm font-medium text-navy">{picked.name} {picked.business_name ? `· ${picked.business_name}` : ""}</span>
                <button onClick={() => setPicked(null)} className="text-xs text-loop hover:underline">Change</button>
              </div>
            ) : (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                <input data-testid="activity-lead-search" value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} placeholder="Search a lead by name, phone, email…" className="h-10 w-full rounded-lg border border-sky pl-9 pr-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
                {leadResults.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-sky bg-white shadow-lg">
                    {leadResults.map((l) => (
                      <button key={l.id} data-testid={`lead-suggestion-${l.id}`} onClick={() => pickLead(l)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-mist">
                        <span className="font-medium text-navy">{l.name}</span>
                        <span className="text-xs text-ink/50">{l.phone || l.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* Templates — generic flow only */}
        {!isEdit && !isMeeting && (
          <div>
            <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-ink/60"><Sparkles className="h-3.5 w-3.5 text-loop" />Quick templates</p>
            <div className="flex flex-wrap gap-1.5">
              {(options?.activity_templates || []).map((t) => (
                <button key={t.id} type="button" data-testid={`template-${t.id}`} onClick={() => applyTemplate(t)} className="rounded-full border border-sky bg-white px-2.5 py-1 text-xs font-medium text-ink/70 transition-colors hover:border-loop hover:text-loop">
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {!isMeeting && (
            <div><label className="mb-1 block text-sm font-medium text-ink/80">Type</label>
              <select data-testid="activity-type-select" value={form.activity_type} onChange={(e) => set("activity_type", e.target.value)} className="h-10 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
                {(options?.activity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div className={isMeeting ? "col-span-2 sm:col-span-1" : ""}><label className="mb-1 block text-sm font-medium text-ink/80">Priority</label>
            <select data-testid="activity-priority-select" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="h-10 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
              {(options?.activity_priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div><label className="mb-1 block text-sm font-medium text-ink/80">{showMeetingFields ? "Meeting title" : "Title"}</label>
          <input data-testid="activity-title-input" value={form.activity_title} onChange={(e) => { titleEdited.current = true; set("activity_title", e.target.value); }} className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
        </div>
        <div><label className="mb-1 block text-sm font-medium text-ink/80">{showMeetingFields ? "Agenda / Notes" : "Note"}</label>
          <textarea data-testid="activity-note-input" value={form.activity_note} onChange={(e) => set("activity_note", e.target.value)} rows={2} placeholder={showMeetingFields ? "Add meeting agenda, context, or discussion points" : placeholder} className="w-full rounded-lg border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
        </div>

        {/* Meeting date & time (DateTimePicker provides the Today/Tomorrow/Day After/After 7 Days quick buttons) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-ink/80">{showMeetingFields ? "Meeting Date & Time" : "Due date/time"}</label>
          <div className="grid grid-cols-2 gap-3">
            <DateTimePicker value={form.due_at} onChange={(v) => set("due_at", v)} testid="activity-due-input" />
            {can("reassign") && (
              <select data-testid="activity-assigned-select" value={form.assigned_to_user_id} onChange={(e) => set("assigned_to_user_id", e.target.value)} className="h-10 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
                <option value="">{showMeetingFields ? "Assign to (lead owner)" : "Lead owner (default)"}</option>
                {users.filter((u) => u.status !== "Inactive").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Internal team members — meeting activities only */}
        {showMeetingFields && <MeetingTeamMembers value={team} onChange={setTeam} />}

        <button onClick={save} disabled={saving} data-testid="activity-save-button" className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-loop font-sora text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : submitLabel}
        </button>
      </DialogContent>
    </Dialog>
  );
}
